import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { Control } from 'react-hook-form';

import type { OpenAPISpec } from '@gitbook/api-client';
import { PlainCodeBlock } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomButton,
    Description,
    DialogRoot,
    DialogTrigger,
    FieldError,
    FieldGroup,
    Label,
    Tab,
    Tabs,
    TextField,
    TextInput,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { apiQuery } from '../../state';
import { CreateAPITokenDialog } from '../APITokens';
import { OpenAPIFileUploadField } from './OpenAPIFileUploadField';

export type OpenAPISourceType = 'url' | 'file' | 'cli';

export interface OpenAPISourceInputs {
    /**
     * URL to sync the OpenAPI spec from (for type 'url')
     */
    url: string | null;
    /**
     * Text content of the OpenAPI spec. (for type 'file')
     */
    text: {
        name: string;
        content: string;
    } | null;
}

export function OpenAPISpecSourceFields<TFieldValues extends OpenAPISourceInputs>(props: {
    type: OpenAPISourceType;
    onTypeChange: (type: OpenAPISourceType) => void;
    control: Control<TFieldValues>;
    specSlug: string;
    organizationId: string;
    disabled?: boolean;
    autoFocus?: boolean;
    onDone: (openAPISpec: OpenAPISpec) => void;
}) {
    const {
        type,
        onTypeChange,
        control,
        disabled,
        autoFocus = true,
        specSlug,
        organizationId,
    } = props;
    return (
        <>
            <Tabs className="mb-4 border-b">
                <Tab
                    active={type === 'url'}
                    leadingIcon={Icon.Link}
                    label="URL"
                    onPress={() => onTypeChange('url')}
                />
                <Tab
                    active={type === 'file'}
                    leadingIcon={Icon.FileCode}
                    label="File"
                    onPress={() => onTypeChange('file')}
                />
                <Tab
                    active={type === 'cli'}
                    leadingIcon={Icon.Code}
                    label="CLI"
                    onPress={() => onTypeChange('cli')}
                />
            </Tabs>
            {(() => {
                switch (type) {
                    case 'url': {
                        return (
                            <TextField name="url" validate="http-url">
                                <Label>Import URL</Label>
                                <Description>
                                    We’ll check for updates every 6 hours and sync your API docs
                                    automatically.
                                </Description>
                                <TextInput
                                    autoFocus={autoFocus}
                                    placeholder="https://api.example.com/openapi.yaml"
                                    type="url"
                                    disabled={disabled}
                                />
                                <FieldError />
                            </TextField>
                        );
                    }
                    case 'file': {
                        return (
                            <FieldGroup>
                                <Label>Upload file</Label>
                                <Description>
                                    Your API docs will update only when you upload a new version.
                                </Description>
                                <OpenAPIFileUploadField
                                    control={control as unknown as Control<OpenAPISourceInputs>}
                                    name="text"
                                    disabled={disabled}
                                />
                                <FieldError />
                            </FieldGroup>
                        );
                    }
                    case 'cli': {
                        if (!specSlug) {
                            return <div className="text-danger">Please enter a name.</div>;
                        }
                        return (
                            <div className="flex flex-col gap-6">
                                <div className="flex flex-col gap-1">
                                    <p>
                                        <span className="mr-1 text-muted">1.</span>
                                        Install GitBook CLI
                                    </p>
                                    <PlainCodeBlock
                                        code="npm install -g @gitbook/cli"
                                        syntax="bash"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <p>
                                        <span className="mr-1 text-muted">2.</span>
                                        <DialogRoot>
                                            <DialogTrigger>
                                                <AtomButton className="underline">
                                                    Create an API token
                                                </AtomButton>
                                            </DialogTrigger>
                                            <CreateAPITokenDialog />
                                        </DialogRoot>{' '}
                                        and then set it in your environment variables.
                                    </p>
                                    <PlainCodeBlock
                                        code="export GITBOOK_TOKEN=<api-token>"
                                        syntax="bash"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <p>
                                        <span className="mr-1 text-muted">3.</span>
                                        Publish your OpenAPI specification
                                    </p>
                                    <PlainCodeBlock
                                        code={`gitbook openapi publish --spec ${specSlug} --organization ${organizationId} <path-to-openapi-file>`}
                                        syntax="bash"
                                    />
                                </div>
                                <WaitingForSpec
                                    organizationId={organizationId}
                                    specSlug={specSlug}
                                    onDone={props.onDone}
                                />
                            </div>
                        );
                    }
                    default:
                        assertNever(type);
                }
            })()}
        </>
    );
}

function WaitingForSpec(props: {
    specSlug: string;
    organizationId: string;
    onDone: (openAPISpec: OpenAPISpec) => void;
}) {
    const { specSlug, organizationId } = props;
    const onDone = useEventCallback(props.onDone);
    const { data, isFetched } = useQuery({
        ...apiQuery('getOpenAPISpecBySlug', { organizationId, specSlug }),
        refetchInterval: 3000,
        staleTime: 1000,
    });
    const initialData = useRef<OpenAPISpec | undefined | null>(undefined);
    useEffect(() => {
        if (isFetched && initialData.current === undefined) {
            initialData.current = data ?? null;
        }
    }, [isFetched, data]);
    useEffect(() => {
        if (data && initialData.current?.updatedAt !== data.updatedAt) {
            onDone(data);
        }
    }, [data, onDone]);
    return (
        <div className="text-ui-standout">
            <Icon.Dot className="animate-pulse" /> Waiting for the OpenAPI spec to be published…
        </div>
    );
}
